import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CostConfirmDialog } from '@/components/ui/cost-confirm-dialog';
import { Input, Label, Select } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Field } from '@/components/triggers/Field';
import { FieldGroup } from '@/components/triggers/FieldGroup';
import { PromptBlock } from '@/components/triggers/PromptBlock';
import {
  EMPTY_PRODUCT,
  SponsoredProductCard,
  claimsFrom,
  type ProductDraft,
} from '@/components/triggers/SponsoredProductCard';
import { VideoLookCard } from '@/components/triggers/VideoLookCard';
import {
  ASPECT_RATIO_OPTIONS,
  CONTENT_GEN_COST_PREVIEW,
  DEFAULT_ASPECT_RATIO,
  DEFAULT_RESOLUTION,
  FIXED_VIDEO_DURATION_SEC,
  LAST_PRODUCT_STORAGE_KEY,
  RESOLUTION_OPTIONS,
  TRIGGERS_PERSONAS_LIMIT,
} from '@/constants/triggers';
import { api } from '@/lib/api';
import type {
  AspectRatio,
  SponsoredProductInput,
  VideoLookInput,
  VideoResolution,
} from '@/types/content';

function loadLastProduct(): ProductDraft {
  try {
    const raw = window.localStorage.getItem(LAST_PRODUCT_STORAGE_KEY);
    return raw
      ? { ...EMPTY_PRODUCT, ...(JSON.parse(raw) as Partial<ProductDraft>) }
      : EMPTY_PRODUCT;
  } catch {
    return EMPTY_PRODUCT;
  }
}

function saveLastProduct(draft: ProductDraft) {
  try {
    window.localStorage.setItem(LAST_PRODUCT_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // Storage blocked (private mode etc.) — remembering is a convenience only.
  }
}

export function TriggerContentPage({ initialPersonaId }: { initialPersonaId?: string }) {
  const navigate = useNavigate();
  const personasQ = useQuery({
    queryKey: ['personas'],
    queryFn: () => api.listPersonas({ limit: TRIGGERS_PERSONAS_LIMIT }),
  });

  const [personaId, setPersonaId] = useState<string>(initialPersonaId ?? '');
  // '' = Auto (least-recently-used rotation, decided by the workflow).
  const [pillar, setPillar] = useState<string>('');
  const [resolution, setResolution] = useState<VideoResolution>(DEFAULT_RESOLUTION);
  const [aspect, setAspect] = useState<AspectRatio>(DEFAULT_ASPECT_RATIO);
  const [seed, setSeed] = useState<string>('');
  const [customRules, setCustomRules] = useState<string[]>([]);
  const [sceneMode, setSceneMode] = useState(false);
  const [productOn, setProductOn] = useState(false);
  const [productDraft, setProductDraft] = useState<ProductDraft>(loadLastProduct);
  const [look, setLook] = useState<VideoLookInput>({ outfit: '', setting: '' });
  const [showPreview, setShowPreview] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!personaId && personasQ.data && personasQ.data.length > 0) {
      const fallback = personasQ.data[0]?.persona_id;
      if (initialPersonaId) setPersonaId(initialPersonaId);
      else if (fallback) setPersonaId(fallback);
    }
  }, [personasQ.data, initialPersonaId, personaId]);

  const persona = useMemo(
    () => (personasQ.data ?? []).find((p) => p.persona_id === personaId),
    [personasQ.data, personaId],
  );
  const heroUrl = persona?.avatar_references?.image_urls?.[0];
  const hasHero = !!heroUrl;
  const pillars = persona?.voice_sheet?.pillars ?? [];

  // Reset the pillar pick when the persona changes — names are per-persona.
  useEffect(() => {
    setPillar('');
  }, [personaId]);

  const cleanRules = customRules.map((r) => r.trim()).filter(Boolean);
  const pillarOrNull = pillar || null;

  const productClaims = claimsFrom(productDraft);
  const product: SponsoredProductInput | null = productOn
    ? {
        name: productDraft.name.trim(),
        brand: productDraft.brand.trim(),
        image_url: productDraft.imageUrl.trim(),
        label_claims: productClaims,
      }
    : null;
  const productReady =
    !product || (!!product.name && !!product.image_url && product.label_claims.length > 0);
  const lookOrNull: VideoLookInput | null =
    look.outfit.trim() || look.setting.trim()
      ? { outfit: look.outfit.trim(), setting: look.setting.trim() }
      : null;

  const previewQ = useQuery({
    queryKey: [
      'content',
      'preview-prompt',
      personaId,
      pillarOrNull,
      cleanRules.join('|'),
      product ? JSON.stringify(product) : '',
    ],
    queryFn: () =>
      api.previewContentPrompt({
        persona_id: personaId,
        brief: null,
        pillar: pillarOrNull,
        custom_rules: cleanRules,
        product,
      }),
    enabled: !!personaId && showPreview,
  });

  const trigger = useMutation({
    mutationFn: () =>
      api.triggerContent({
        persona_id: personaId,
        brief: null,
        pillar: pillarOrNull,
        resolution,
        aspect_ratio: aspect,
        seed: seed.trim() ? Number(seed.trim()) : null,
        custom_rules: cleanRules,
        scene_mode: sceneMode,
        product,
        look: lookOrNull,
      }),
    onSuccess: (resp) => {
      setConfirmOpen(false);
      if (product) saveLastProduct(productDraft);
      toast.success(`Started content-gen ${resp.entity_id}`, {
        description: `workflow: ${resp.workflow_id}`,
      });
      void navigate({ to: '/runs/$workflowId', params: { workflowId: resp.workflow_id } });
    },
    onError: (err) => {
      toast.error('Trigger failed', { description: String(err) });
    },
  });

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Trigger content generation</h1>
        <p className="text-sm text-muted-foreground">
          Spins up a 20-second content piece on top of an existing persona. ~4–8 minutes including
          four Kling segment renders + ffmpeg concat. The script is graded by a critique pass before
          any video is rendered. Cost guardrail: max 2 attempts per segment. Provider is hard-coded
          to Kling.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Persona</CardTitle>
        </CardHeader>
        <CardContent>
          {personasQ.isLoading ? (
            <Skeleton className="h-12" />
          ) : (personasQ.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No personas exist yet. Generate one first via Trigger → Persona.
            </p>
          ) : (
            <div className="flex items-center gap-3">
              <Avatar
                src={heroUrl ?? null}
                alt={persona?.voice_sheet?.display_name ?? ''}
                size="lg"
              />
              <div className="flex-1 space-y-1.5">
                <Label>Persona</Label>
                <Select value={personaId} onChange={(e) => setPersonaId(e.target.value)}>
                  {(personasQ.data ?? []).map((p) => (
                    <option key={p.persona_id} value={p.persona_id}>
                      {(p.voice_sheet?.display_name ?? 'persona') +
                        ' · ' +
                        p.archetype +
                        ' · ' +
                        p.persona_id.slice(0, 14) +
                        '…'}
                    </option>
                  ))}
                </Select>
                {persona && !hasHero && (
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    This persona has no avatar reference images — image-to-video requires at least
                    one. Re-run persona generation.
                  </p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {persona && (
        <Card>
          <CardHeader>
            <CardTitle>Persona context</CardTitle>
            <CardDescription>
              Read-only preview of what flows into Claude's script prompt as the persona's voice
              sheet. Edit the persona itself (re-run persona-gen) if any of this looks off.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <Field label="Content pillar">
                {pillars.length === 0 ? (
                  <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                    This persona has no pillars (generated before pillars existed). Re-run
                    persona-gen to get three; until then posts aren't rotated.
                  </div>
                ) : (
                  <>
                    <Select value={pillar} onChange={(e) => setPillar(e.target.value)}>
                      <option value="">Auto — rotate least-recently-used</option>
                      {pillars.map((pl) => (
                        <option key={pl.name} value={pl.name}>
                          {pl.name}
                          {pl.description ? ` — ${pl.description}` : ''}
                        </option>
                      ))}
                    </Select>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Every post is assigned to one of the persona's three pillars. Auto picks the
                      one that hasn't been posted to for the longest.
                    </p>
                  </>
                )}
              </Field>
            </div>
            <FieldGroup label="Bio" body={persona.voice_sheet?.bio} />
            <FieldGroup label="Topics" tags={persona.voice_sheet?.topics} />
            <FieldGroup label="Tone" tags={persona.voice_sheet?.tone} />
            <FieldGroup label="Speech quirks" tags={persona.voice_sheet?.speech_quirks} />
            <FieldGroup label="Do" tags={persona.voice_sheet?.dos} />
            <FieldGroup label="Don't" tags={persona.voice_sheet?.donts} />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Video</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Duration">
            <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
              {FIXED_VIDEO_DURATION_SEC}s · 4× 5s Kling segments concatenated
            </div>
          </Field>
          <Field label="Resolution">
            <Select
              value={resolution}
              onChange={(e) => setResolution(e.target.value as VideoResolution)}
            >
              {RESOLUTION_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Aspect ratio">
            <Select value={aspect} onChange={(e) => setAspect(e.target.value as AspectRatio)}>
              {ASPECT_RATIO_OPTIONS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Seed (optional)">
            <Input
              type="number"
              value={seed}
              onChange={(e) => setSeed(e.target.value)}
              placeholder="leave blank for random"
            />
          </Field>
        </CardContent>
      </Card>

      <SponsoredProductCard
        enabled={productOn}
        onEnabledChange={setProductOn}
        draft={productDraft}
        onChange={setProductDraft}
      />

      <VideoLookCard look={look} onChange={setLook} />

      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0 gap-3">
          <div className="space-y-1">
            <CardTitle>Custom script rules</CardTitle>
            <CardDescription>
              Per-run rules appended to Claude's script prompt as a bullet list. Use for constraints
              like <code>no questions in the caption</code> or{' '}
              <code>must name one specific exercise</code>. The system rules (niche-specific,
              hashtag style, hook + next-action structure) always apply on top.
            </CardDescription>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 whitespace-nowrap"
            onClick={() => setCustomRules([...customRules, ''])}
          >
            <Plus className="h-3.5 w-3.5" /> Add rule
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {customRules.length === 0 && (
            <p className="text-xs text-muted-foreground">No custom rules.</p>
          )}
          {customRules.map((rule, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                value={rule}
                onChange={(e) =>
                  setCustomRules(customRules.map((x, j) => (i === j ? e.target.value : x)))
                }
                placeholder="e.g. avoid motivational clichés"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setCustomRules(customRules.filter((_, j) => i !== j))}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}

          <label className="mt-2 flex cursor-pointer items-start gap-2 border-t border-border pt-3 text-sm text-foreground">
            <input
              type="checkbox"
              checked={sceneMode}
              onChange={(e) => setSceneMode(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
            />
            <span>
              Compose an establishing scene frame
              <span className="block text-xs text-muted-foreground">
                For scene-based rules (e.g. "at the chain-link fence") — builds the setting into the
                opening frame so it doesn't pop in. Uses your custom rules above; adds one image gen
                (~1–2 min) + cost.
              </span>
            </span>
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="flex w-full items-center justify-between gap-3 text-left"
          >
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2 text-sm">
                {showPreview ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
                Prompt preview
              </CardTitle>
              <CardDescription>
                Exact system + user prompt that will be sent to Claude when you trigger.
                {!showPreview && ' Click to expand.'}
              </CardDescription>
            </div>
          </button>
        </CardHeader>
        {showPreview && (
          <CardContent className="space-y-4">
            {!personaId ? (
              <p className="text-xs italic text-muted-foreground">Select a persona first.</p>
            ) : previewQ.isLoading ? (
              <Skeleton className="h-32" />
            ) : previewQ.isError ? (
              <p className="text-xs text-destructive">
                Failed to render preview: {String(previewQ.error)}
              </p>
            ) : previewQ.data ? (
              <>
                <PromptBlock label="System" body={previewQ.data.system} />
                <PromptBlock label="User" body={previewQ.data.user} />
              </>
            ) : null}
          </CardContent>
        )}
      </Card>

      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {cleanRules.length === 0
            ? 'No custom rules — system prompt only.'
            : `${cleanRules.length} custom rule(s) will be appended.`}
          {product &&
            (productReady
              ? ` Featuring ${product.name}.`
              : ' Finish the product details to start.')}
          {lookOrNull && ' Custom outfit/setting.'}
        </p>
        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={trigger.isPending || !hasHero || !personaId || !productReady}
        >
          {trigger.isPending ? 'Triggering…' : 'Start content generation'}
        </Button>
      </div>

      {confirmOpen && (
        <CostConfirmDialog
          costUsd={CONTENT_GEN_COST_PREVIEW.cost_usd}
          durationSec={CONTENT_GEN_COST_PREVIEW.duration_sec}
          currency={CONTENT_GEN_COST_PREVIEW.currency}
          summary={CONTENT_GEN_COST_PREVIEW.summary}
          pending={trigger.isPending}
          onConfirm={() => trigger.mutate()}
          onCancel={() => setConfirmOpen(false)}
        />
      )}
    </div>
  );
}
